/**
 * B1020 region 2 (a) — the tickets page's cart wears the gift cards on it
 * (855:268 @1440 · 855:2 @390). The pure half: `cart.get`'s `giftCards`
 * narrowed to rows, the two doors between the pages, and the note.
 * Pure: no React, no I/O. A malformed row is DROPPED, never guessed — the
 * cart's total comes from the kernel, not from these rows.
 */
import type { Money } from "./money.js";
import { slotWords, type WhenOptions } from "./when.js";

export interface GiftCardCartRow {
  readonly lineId: string;
  /** Who it is for — the row's name ("Gift card · to Maya Chen"). */
  readonly to: string;
  /** Where it goes, when the buyer typed one. */
  readonly email?: string;
  /** "Sends right after you pay" · "Sends Fri 6 · 9:00 AM". */
  readonly sends: string;
  readonly amount: Money;
  /** B1020 (d.3) — who it is from, as the form took it (855:268's "from Priya"). */
  readonly from?: string;
  /** B1020 (d.3) — the note, when the buyer wrote one. */
  readonly message?: string;
}

const isMoney = (m: unknown): m is Money =>
  typeof m === "object" &&
  m !== null &&
  Number.isInteger((m as Money).amount) &&
  (m as Money).amount > 0 &&
  typeof (m as Money).currency === "string";

/** `cart.get`'s answer → the gift-card rows, in the cart's order. */
export function giftCardRows(res: unknown, when: WhenOptions): readonly GiftCardCartRow[] {
  const cards = typeof res === "object" && res !== null ? (res as { giftCards?: unknown }).giftCards : undefined;
  if (!Array.isArray(cards)) return [];
  const rows: GiftCardCartRow[] = [];
  for (const c of cards as unknown[]) {
    if (typeof c !== "object" || c === null) continue;
    const r = c as Record<string, unknown>;
    if (typeof r["lineId"] !== "string" || !isMoney(r["amount"])) continue;
    const sendAt = typeof r["sendAt"] === "number" && Number.isFinite(r["sendAt"]) ? r["sendAt"] : undefined;
    rows.push({
      lineId: r["lineId"],
      to: typeof r["recipientName"] === "string" && r["recipientName"] !== "" ? r["recipientName"] : "someone",
      ...(typeof r["recipientEmail"] === "string" && r["recipientEmail"] !== "" ? { email: r["recipientEmail"] } : {}),
      sends: sendAt === undefined ? "Sends right after you pay" : `Sends ${slotWords(sendAt, when)}`,
      amount: r["amount"],
      ...(typeof r["senderName"] === "string" && r["senderName"].trim() !== "" ? { from: r["senderName"].trim() } : {}),
      ...(typeof r["message"] === "string" && r["message"].trim() !== "" ? { message: r["message"].trim() } : {}),
    });
  }
  return rows;
}

/** The message's start on a row: the first ~20 characters, cut at a word, then an ellipsis. */
function clip(message: string): string {
  if (message.length <= 22) return message;
  const cut = message.slice(0, 22);
  const at = cut.lastIndexOf(" ");
  return `${(at > 8 ? cut.slice(0, at) : cut).trimEnd()} …`;
}

/**
 * B1020 (d.3, 855:268) — a card row's two fact lines: who it goes to (and
 * where), then when it sends, from whom, and the message's start — or
 * "no message", so a missing note reads as a choice, not a gap.
 */
export function giftCardFacts(row: GiftCardCartRow): readonly [string, string] {
  const to = row.email !== undefined ? `To ${row.to} · ${row.email}` : `To ${row.to}`;
  const when = [row.sends, ...(row.from !== undefined ? [`from ${row.from}`] : []), row.message !== undefined ? `“${clip(row.message)}”` : "no message"];
  return [to, when.join(" · ")];
}

/** "Gift card · to Sam" — a summary line names the first name only (855:268's PAY card). */
export function giftCardSumWords(row: GiftCardCartRow): string {
  return `Gift card · to ${row.to.split(/\s+/)[0] ?? row.to}`;
}

/** What a gift card line can't do (855:268's note) — one sentence, said once. */
export const GIFT_CARD_CART_NOTE = "Promo codes and other gift cards don’t apply to gift cards — the full amount is what’s loaded.";

/** The form (ADD ANOTHER GIFT CARD). */
export function giftCardsHref(tenantId: string): string {
  return `/gift-cards?tenant=${encodeURIComponent(tenantId)}`; // vocab-ok: a URL query key (B830 tenant addressing), never rendered
}

/** B1020 (2b) — a card row's EDIT: the form, loading that card. */
export function giftCardEditHref(tenantId: string, lineId: string): string {
  return `/gift-cards?tenant=${encodeURIComponent(tenantId)}&edit=${encodeURIComponent(lineId)}`; // vocab-ok: URL query keys, never rendered
}

/** The tickets page with the cart open (CHECKOUT from the form). */
export function cartOpenHref(tenantId: string): string {
  return `/?tenant=${encodeURIComponent(tenantId)}&cart=open`; // vocab-ok: a URL query key, never rendered
}

/** True only for the exact `cart=open` word — anything else is just the tickets page. */
export function cartOpenRequested(search: string): boolean {
  try {
    return new URLSearchParams(search).get("cart") === "open";
  } catch {
    return false;
  }
}

/* ------------------------------------------------------------- Pay's copies */

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * B1020 (2b, 855:131) — "email me a copy" is asked on the form, the address
 * at Pay. Before the order is placed, each card whose copy this tab recorded
 * goes back WHOLE (`cart.gift_card.update` replaces a card) with the buyer's
 * address as `senderEmail`. Nothing for a card that already carries it, an
 * empty or malformed address, or a card with its copy off. The cartId is the
 * caller's to add.
 */
export function copyUpdates(cartRes: unknown, copies: Readonly<Record<string, boolean>>, email: string): readonly Record<string, unknown>[] {
  const to = email.trim();
  if (!EMAIL.test(to) || to.length > 254) return [];
  const cards = typeof cartRes === "object" && cartRes !== null ? (cartRes as { giftCards?: unknown }).giftCards : undefined;
  if (!Array.isArray(cards)) return [];
  const out: Record<string, unknown>[] = [];
  for (const c of cards as unknown[]) {
    if (typeof c !== "object" || c === null) continue;
    const r = c as Record<string, unknown>;
    const amount = (r["amount"] as { amount?: unknown } | undefined)?.amount;
    if (typeof r["lineId"] !== "string" || typeof amount !== "number" || copies[r["lineId"]] !== true || r["senderEmail"] === to) continue;
    const str = (k: string) => (typeof r[k] === "string" && r[k] !== "" ? { [k]: r[k] } : {});
    out.push({
      lineId: r["lineId"],
      amount,
      ...str("designId"),
      ...str("recipientName"),
      ...str("recipientEmail"),
      ...str("senderName"),
      senderEmail: to,
      ...str("message"),
      ...(typeof r["sendAt"] === "number" ? { sendAt: r["sendAt"] } : {}),
    });
  }
  return out;
}

/** What Pay's email field is for (855:131): tickets, the receipt, the card copies — only what this cart sends. */
export function payEmailLabel(cart: { readonly tickets: boolean; readonly cards: number; readonly copies: boolean }): string {
  if (cart.cards === 0) return "Email for your tickets";
  if (cart.copies) return cart.tickets ? "Email for your tickets, receipt and card copies" : "Email for your receipt and card copies";
  return cart.tickets ? "Email for your tickets and receipt" : "Email for your receipt";
}

/* ------------------------------------------------------- the head's door */

const ON_SALE_KEY = "transact.storefront.giftCardsOnSale";

/** Whether the head's GIFT CARDS door stood on this tab's last load (the offer is asked once per mint). */
export function recallGiftCardsOnSale(storage: { getItem(k: string): string | null } | null): boolean {
  try {
    return storage?.getItem(ON_SALE_KEY) === "1";
  } catch {
    return false;
  }
}

export function rememberGiftCardsOnSale(storage: { setItem(k: string, v: string): void } | null, on: boolean): void {
  try {
    storage?.setItem(ON_SALE_KEY, on ? "1" : "0");
  } catch {
    /* memory-only: the next mint asks again */
  }
}
